import { ColorDefinition, DataVisualization } from "@/types/colors";
import { AppSettingType, Co2MaterialType } from "@api/apiClient";
import BarChart from "@components/Charts/BarChart";
import useAppStore from "@hooks/useAppStore";
import useCompareMaterialsStore from "@hooks/useCompareMaterialsStore";
import i18n from "@i18n/i18n";
import { Box } from "@mui/material";
import { ElementMetrics } from "@stores/CompareMaterialsStore";
import sortArrays from "@utils/sortArrays";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { useEffect, useRef } from "react";
import useState from "react-usestateref";
import useResizeObserver from "use-resize-observer";

export interface MaterialSelectCompareProps {
  onMaterialSelect: (co2MaterialType: Co2MaterialType | null | undefined) => void;
}

const MaterialSelectCompare = observer((props: MaterialSelectCompareProps) => {
  const appStore = useAppStore();
  const compareMaterialsStore = useCompareMaterialsStore();
  const chartRef = useRef(null);
  const { width: chartWidth, height: chartHeight } = useResizeObserver({ ref: chartRef });
  const [colorPalette, setColorPalette] = useState<ColorDefinition[]>([]);
  const [chartData, setChartData] = useState<any>([
    [i18n.t("material-select-compare.name.title"), i18n.t("material-select-compare.name.aToD.title"), { role: "style" }, { role: "annotation" }],
    ["", 0, "#FFFFFF", ""],
  ]);
  const [co2MaterialTypeIds, setCo2MaterialTypeIds, co2MaterialTypeIdsRef] = useState<string[]>([]);

  const splitStringByLength = (str: string | undefined, chunkLength: number) => {
    //console.log("splitStringByLength: ", str, Math.floor(chunkLength));
    const regex = new RegExp(`.{1,${Math.floor(chunkLength)}}`, "g");
    return str?.match(regex) || [];
  };

  const getMaterialTotalCo2 = (metrics: ElementMetrics, co2MaterialType: Co2MaterialType): number => {
    const co2Phases = ["a1A2A3", "a4", "a5", "b1", "b2", "b3", "b4", "b5", "c1", "c2", "c3", "c4", "d", "biogenic"];

    // Sum all valid CO₂ phase values (null/undefined are treated as 0)
    const totalCo2Impact = co2Phases.reduce((sum, key) => {
      const value = (co2MaterialType as any)[key];
      return sum + (typeof value === "number" ? value : 0);
    }, 0);

    switch (co2MaterialType.unit) {
      case "m":
        return metrics.length.total * totalCo2Impact * 0.001;
      case "m2":
        return metrics.area.total * totalCo2Impact * 0.001;
      case "m3":
        return metrics.volume.total * totalCo2Impact * 0.001;
      default:
        return 0;
    }
  };

  const updateChartData = () => {
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, compareMaterialsStore.co2MaterialTypes.length + 1);
    setColorPalette(colorPalette);

    const labelLength = Math.max(10, (chartWidth || 400) / 10);
    const chartValues = Array.from(compareMaterialsStore.co2MaterialTypes, (co2MaterialType, index) => [
      `${splitStringByLength(`${co2MaterialType.name}`, labelLength).join("\n")}\n${i18n.t("material-select-compare.a1A2A3.title")}: ${i18n.t("number", { value: co2MaterialType.a1A2A3, digits: AppSettingType.NumberFormat_EC_Digits })}, ${i18n.t("material-select-compare.unit.title")}: ${co2MaterialType.unit}`,
      getMaterialTotalCo2(compareMaterialsStore.elementMetrics, co2MaterialType),
      colorPalette[index].hex,
      i18n.t("number", { value: getMaterialTotalCo2(compareMaterialsStore.elementMetrics, co2MaterialType), digits: AppSettingType.NumberFormat_EC_Digits }),
    ]);
    chartValues.push([
      i18n.t("material-select-compare.selection.title"),
      compareMaterialsStore.elementMetrics.co2eTotal,
      colorPalette[colorPalette.length - 1].hex,
      i18n.t("number", { value: compareMaterialsStore.elementMetrics.co2eTotal, digits: AppSettingType.NumberFormat_EC_Digits }),
    ]);

    const newCo2MaterialTypeIds = Array.from(compareMaterialsStore.co2MaterialTypes, (co2MaterialType) => co2MaterialType.co2MaterialTypeId! || co2MaterialType.externalId!);
    newCo2MaterialTypeIds.push(""); // selection has no id

    const results = sortArrays([chartValues, newCo2MaterialTypeIds], (a, b) => Number(a[1]) - Number(b[1])) as any[][];

    const chartData = [[i18n.t("material-select-compare.name.title"), i18n.t("material-select-compare.name.aToD.title"), { role: "style" }, { role: "annotation" }], ...results[0]];
    setChartData(chartData);
    setCo2MaterialTypeIds(results[1]);

    console.log("updateChartData compareMaterialsStore.co2MaterialTypes", toJS(compareMaterialsStore.co2MaterialTypes));
    console.log("updateChartData co2MaterialTypeIds: ", results[1]);
  };

  useEffect(() => {
    updateChartData();
  }, []);

  useEffect(() => {
    updateChartData();
  }, [compareMaterialsStore.co2MaterialTypes]);

  const handleChartSelection = async (colIndex?: number | undefined) => {
    console.log("handleChartSelection compareMaterialsStore.co2MaterialTypes", toJS(compareMaterialsStore.co2MaterialTypes));
    console.log("handleChartSelection co2MaterialTypeIds: ", co2MaterialTypeIds);

    if (colIndex === undefined) {
      console.log("handleChartSelection: ", undefined);
      props.onMaterialSelect(undefined);
      return;
    }
    if (props.onMaterialSelect) {
      const co2MaterialTypeId = co2MaterialTypeIdsRef.current[colIndex]; // co2MaterialTypeIds was not updating unless I used the ref
      const co2MaterialType = compareMaterialsStore.co2MaterialTypes.find((m) => m.co2MaterialTypeId === co2MaterialTypeId || m.externalId === co2MaterialTypeId);
      console.log("handleChartSelection: ", co2MaterialType);
      props.onMaterialSelect(co2MaterialType);
    }
  };

  const chartOptions = {
    title: i18n.t("material-select-compare.title.emissions-by-material"),
    hAxis: {
      title: i18n.t("material-select-compare.title.co2-material-types"),
    },
    vAxis: {
      title: i18n.t("material-select-compare.title.ec-materials"),
    },
    chartArea: {
      top: 60,
      left: (chartWidth || 400) * 0.5,
      height: (chartHeight || 400) - 140,
    },
  };

  console.log("chartData: ", chartData);
  console.log("width: ", chartWidth);
  console.log("height: ", chartHeight);

  return (
    <Box sx={{ display: "flex", flexGrow: 1, flexShrink: 0, height: "100%", minWidth: 0 }}>
      <Box ref={chartRef} sx={{ display: "flex", position: "relative", flexGrow: 1, height: "100%", overflow: "hidden" }}>
        <BarChart
          id={`chart1`}
          width={(chartWidth || 300) - 16}
          height={(chartHeight || 300) - 8}
          data={chartData}
          chartOptions={chartOptions}
          colors={colorPalette}
          slantedText={false}
          chartType="BarChart"
          onSelect={handleChartSelection}
        />
      </Box>
    </Box>
  );
});

export default MaterialSelectCompare;
